import { useQuery } from '@tanstack/react-query';
import { usePaymentService } from '../services/payment.service';
import { Addon, AddonApiKey } from '../services/payment.types';

export interface UserPlanStatus {
    userPlans: Record<string, any>;
    isLoadingUserPlans: boolean;
    isPurchased: (year: string) => boolean;
    hasExpertReview: (year: string) => boolean;
    getPurchasedPackage: (year: string) => string;
    hasAddon: (apiKey: AddonApiKey) => boolean;
    hasWeDoEverythingAccessToYear: (year: string) => boolean;
    isWeDoEverythingBoughtButNotValidated: () => boolean;
    hasAnyPurchases: () => boolean;
}

/**
 * Custom hook to check if a user has purchased access to specific tax years
 * This hook provides a consistent way to check user plan status across the application
 */
export const useUserPlanStatus = (): UserPlanStatus => {
    const { getUserPlans } = usePaymentService();

    const { data: userPlans = {}, isLoading: isLoadingUserPlans } = useQuery({
        queryKey: ['userPlans'],
        queryFn: async () => {
            return getUserPlans();
        },
    });

    /**
     * Check if "We Do Everything" addon allows access to a specific tax year
     * @param year The tax year to check
     * @returns True if the addon allows access to the tax year, false otherwise
     */
    const hasWeDoEverythingAccessToYear = (year: string): boolean => {
        const addon = getAddon(AddonApiKey.WE_DO_EVERYTHING);

        if (!addon || addon.status !== 'active') {
            return false;
        }

        // If validated is not present, null, undefined, or false, unlock all tax reports
        if (addon.validated === undefined || addon.validated === null || addon.validated === false) {
            return true;
        }

        // If validated is true, check validUntil date
        if (addon.validUntil) {
            let validUntil: Date;
            if (typeof addon.validUntil === 'string') {
                validUntil = new Date(addon.validUntil);
            } else if (addon.validUntil && typeof addon.validUntil === 'object' && '_seconds' in addon.validUntil) {
                validUntil = new Date((addon.validUntil as any)._seconds * 1000);
            } else {
                validUntil = new Date(addon.validUntil as any);
            }

            const purchaseYear = validUntil.getFullYear();
            const taxYear = parseInt(year);
            const hasAccess = taxYear <= purchaseYear;
            return hasAccess;
        }

        console.log(`hasWeDoEverythingAccessToYear: Validated is true but no validUntil date for year ${year}`);
        return false;
    };

    /**
     * Check if a specific tax year is purchased
     * @param year The tax year to check
     * @returns True if the tax year is purchased, false otherwise
     */
    const isPurchased = (year: string): boolean => {
        if (hasAddon(AddonApiKey.WE_DO_EVERYTHING) && hasWeDoEverythingAccessToYear(year)) {
            return true;
        }
        const planKey = `taxReports.${year}`;
        return userPlans[planKey]?.status === 'active';
    };

    /**
     * Check if expert review is purchased for a specific tax year
     * @param year The tax year to check
     * @returns True if expert review is purchased, false otherwise
     */
    const hasExpertReview = (year: string): boolean => {
        const planKey = `taxReports.${year}`;
        return userPlans[planKey]?.expertReview === true && userPlans[planKey]?.status === 'active';
    };

    /**
     * Get the purchased package for a specific tax year
     * @param year The tax year to check
     * @returns The purchased package or an empty string if none
     */
    const getPurchasedPackage = (year: string): string => {
        if (!isPurchased(year)) return '';

        const planKey = `taxReports.${year}`;
        return userPlans[planKey]?.package || '';
    };

    /**
     * Check if a specific addon is purchased and active
     * @param apiKey The API key of the addon
     * @returns True if the addon is purchased and active, false otherwise
     */
    const hasAddon = (apiKey: AddonApiKey): boolean => {
        const addon = getAddon(apiKey);


        // Check if the addon exists, is active, and not expired
        if (!addon || addon.status !== 'active') {
            return false;
        }

        // For "We Do Everything" addon, don't check validUntil expiration here
        // The expiration logic is handled in hasWeDoEverythingAccessToYear
        if (apiKey === AddonApiKey.WE_DO_EVERYTHING) {
            return true;
        }

        // Check if the addon is still valid (not expired) for other addons
        if (addon.validUntil) {
            const validUntil = new Date(addon.validUntil as any);
            const now = new Date();
            const isExpired = validUntil < now;
            if (isExpired) return false;
        }

        return true;
    };

    const getAddon = (apiKey: AddonApiKey): Addon | undefined => {
        const addonKey = `addons.${apiKey}`;
        return userPlans[addonKey];
    };

    /**
     * Check if "We Do Everything" addon is bought but not validated
     * @returns True if the addon is active but not validated, false otherwise
     */
    const isWeDoEverythingBoughtButNotValidated = (): boolean => {
        const addon = getAddon(AddonApiKey.WE_DO_EVERYTHING);

        if (!addon || addon.status !== 'active') {
            return false;
        }

        // If validated is undefined, null, or false, the addon is bought but not validated
        return addon.validated === undefined || addon.validated === null || addon.validated === false;
    };

    /**
     * Check if user has any purchases (tax reports or addons)
     * @returns True if user has any active purchases, false otherwise
     */
    const hasAnyPurchases = (): boolean => {
        if (!userPlans) return false;
        
        // Check for any tax report purchases
        const hasTaxReports = Object.keys(userPlans).some(key => {
            return key.startsWith('taxReports.') && userPlans[key]?.status === 'active';
        });
        
        // Check for any addon purchases
        const hasAddons = Object.keys(userPlans).some(key => {
            return key.startsWith('addons.') && userPlans[key]?.status === 'active';
        });
        
        return hasTaxReports || hasAddons;
    };

    return {
        userPlans,
        isLoadingUserPlans,
        isPurchased,
        hasExpertReview,
        getPurchasedPackage,
        hasAddon,
        hasWeDoEverythingAccessToYear,
        isWeDoEverythingBoughtButNotValidated,
        hasAnyPurchases,
    };
};
